import { queryOptions } from "@tanstack/react-query";
import { QUERIES } from "modules/common/query";
import { API_ROUTES } from "utils/api-route.constants";
import {
  Notification,
  NotificationCounts,
  NotificationGroup,
} from "modules/chat/model/chat";
import {
  getChatContext,
  getTradeContext,
  isChatNotification,
  isProposalNotification,
  isTradeNotification,
} from "modules/notifications/utils/notificationUtils";
import { clientLogger } from "modules/common/logger/client";

interface NotificationOptionsParams {
  userId: string;
  fetchWrapper: {
    get: (url: string, params: unknown) => Promise<unknown>;
  };
}

export interface NotificationData {
  // Core sorted notifications
  notifications: Notification[];

  // Filtered by type
  chatNotifications: Notification[];
  tradeNotifications: Notification[];
  proposalNotifications: Notification[];
  unreadNotifications: Notification[];
  unreadChatNotifications: Notification[];
  unreadTradeNotifications: Notification[];
  unreadProposalNotifications: Notification[];

  // Counts and flags
  unreadCount: number;
  hasUnreadNotifications: boolean;
  hasUnreadChatNotifications: boolean;
  hasUnreadTradeNotifications: boolean;
  hasUnreadProposalNotifications: boolean;

  // Latest notifications
  latestChatNotification: Notification | null;

  // Chat-specific backward compatibility
  unreadCountsByChatId: Record<string, number>;

  // New structures
  notificationCounts: NotificationCounts;
  notificationGroups: NotificationGroup[];
}

/**
 * Query options factory for notifications with all derived values computed in select
 *
 * Benefits:
 * - ALL transformations memoized by React Query (sorting, filtering, grouping, counts)
 * - Single source of truth for all notification data
 * - Eliminates multiple useMemo blocks and useCallback functions
 * - Re-renders only when actual computed values change, not intermediate data
 */
export function notificationOptions({
  userId,
  fetchWrapper,
}: NotificationOptionsParams) {
  return queryOptions({
    queryKey: [QUERIES.NOTIFICATIONS, userId],
    queryFn: async (): Promise<Notification[]> => {
      try {
        const response = await fetchWrapper.get(
          API_ROUTES.NOTIFICATIONS.BASE,
          null
        );
        return response as Notification[];
      } catch (error) {
        clientLogger.error("notifications.fetch", "fetch notifications failed", error);
        throw error;
      }
    },
    // Compute ALL derived values in select - memoized by React Query
    select: (rawNotifications): NotificationData => {
      // 1. Sort notifications by timestamp
      const notifications = [...rawNotifications].sort((a, b) => {
        const getNotificationTimestamp = (notification: Notification) => {
          const timestamp = new Date(notification.updatedAt).getTime();
          return isNaN(timestamp)
            ? new Date(notification.createdAt).getTime()
            : timestamp;
        };

        return getNotificationTimestamp(b) - getNotificationTimestamp(a);
      });

      // 2. Filter by type and status
      const chatNotifications = notifications.filter(isChatNotification);
      const tradeNotifications = notifications.filter(isTradeNotification);
      const proposalNotifications = notifications.filter(isProposalNotification);
      const unreadNotifications = notifications.filter(
        (n) => n.status === "unread"
      );
      const unreadChatNotifications = chatNotifications.filter(
        (n) => n.status === "unread"
      );
      const unreadTradeNotifications = tradeNotifications.filter(
        (n) => n.status === "unread"
      );
      const unreadProposalNotifications = proposalNotifications.filter(
        (n) => n.status === "unread"
      );

      // 3. Build unread counts by chatId (backward compatibility)
      const unreadCountsByChatId = unreadChatNotifications.reduce(
        (acc, notification) => {
          const chatContext = getChatContext(notification);
          if (chatContext?.chatId) {
            acc[chatContext.chatId] = (acc[chatContext.chatId] || 0) + 1;
          }
          return acc;
        },
        {} as Record<string, number>
      );

      // 4. Get latest chat notification
      const latestChatNotification =
        chatNotifications
          .filter((n) => n.status === "unread")
          .sort(
            (a, b) =>
              new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
          )[0] || null;

      // 5. Build notification counts
      const notificationCounts: NotificationCounts = {
        total: unreadNotifications.length,
        unread: unreadNotifications.length,
        chat: unreadChatNotifications.length,
        trade: unreadTradeNotifications.length,
        proposal: unreadProposalNotifications.length,
      };

      // 6. Group notifications by type
      const getNotificationsByDomain = (
        domain: "chat" | "trade" | "proposal",
        tradeType?: "trade-item-available" | "trade-item-wanted"
      ): Notification[] => {
        return notifications.filter((n) => {
          if (n.domain === "chat" && domain === "chat") return true;
          if (n.domain === "proposal" && domain === "proposal") return true;
          if (n.domain === "trade" && domain === "trade") {
            if (tradeType) {
              const tradeContext = getTradeContext(n);
              return tradeContext?.type === tradeType;
            }
            return true;
          }
          return false;
        });
      };

      const groups = [
        {
          domain: "chat" as const,
          tradeType: undefined,
          type: "chat" as const,
        },
        {
          domain: "proposal" as const,
          tradeType: undefined,
          type: "proposal" as const,
        },
        {
          domain: "trade" as const,
          tradeType: "trade-item-available" as const,
          type: "trade-item-available" as const,
        },
        {
          domain: "trade" as const,
          tradeType: "trade-item-wanted" as const,
          type: "trade-item-wanted" as const,
        },
      ];

      const notificationGroups: NotificationGroup[] = groups.map((group) => {
        const items = getNotificationsByDomain(group.domain, group.tradeType);
        const unreadItems = items.filter((n) => n.status === "unread");
        const latest = items.length > 0 ? items[0] : null;

        return {
          type: group.type,
          count: unreadItems.length,
          latest: latest || undefined,
          items: items.slice(0, 5), // Recent 5 for dropdown
        };
      });

      // Return all computed values
      return {
        notifications,
        chatNotifications,
        tradeNotifications,
        proposalNotifications,
        unreadNotifications,
        unreadChatNotifications,
        unreadTradeNotifications,
        unreadProposalNotifications,
        unreadCount: unreadNotifications.length,
        hasUnreadNotifications: unreadNotifications.length > 0,
        hasUnreadChatNotifications: unreadChatNotifications.length > 0,
        hasUnreadTradeNotifications: unreadTradeNotifications.length > 0,
        hasUnreadProposalNotifications: unreadProposalNotifications.length > 0,
        latestChatNotification,
        unreadCountsByChatId,
        notificationCounts,
        notificationGroups,
      };
    },
    enabled: !!userId,
    // refetchInterval: 5000, // Poll every 5 seconds
    refetchIntervalInBackground: false, // 🔥 Continue polling when tab is hidden
    refetchOnWindowFocus: true, // Extra refresh when tab becomes active
    refetchOnMount: true,
    // staleTime: 0, // Always consider data stale to ensure fresh polling
  });
}
